import { useMemo } from 'react';
import {
  DEFAULT_AVATAR,
  looksLikeOpaqueTechnicalId,
  memberDisplayNameForUi,
} from '../../../../../shared/utils/matrix/matrixRoomUtils';

/**
 * Local + remote display names for an active call in a room (prefers Matrix member state over URL params).
 * @param {Object|null|undefined} room - Matrix Room
 * @param {Object|null|undefined} client - MatrixClient
 * @param {string|null|undefined} preferredOtherUserId - Optional Matrix user id (or substring) of the peer
 * @returns {{ local: string, remote: string|null }}
 */
const getCallParticipantDisplayNames = (room, client, preferredOtherUserId = null) => {
  const fallbackLocal = 'You';
  if (!room || !client) {
    return { local: fallbackLocal, remote: null };
  }
  const myUserId = client.getUserId?.();
  if (!myUserId) {
    return { local: fallbackLocal, remote: null };
  }
  const members = room.getJoinedMembers?.() || [];
  if (!members.length) {
    return { local: fallbackLocal, remote: null };
  }
  const selfMember = members.find((m) => m.userId === myUserId);
  const local = memberDisplayNameForUi(selfMember) || fallbackLocal;

  let otherMember = null;
  if (preferredOtherUserId) {
    otherMember =
      members.find((m) => m.userId === preferredOtherUserId) ||
      members.find((m) => String(m?.userId || '').includes(preferredOtherUserId));
  }
  if (!otherMember) {
    otherMember = members.find((m) => m.userId !== myUserId);
  }
  const remote = memberDisplayNameForUi(otherMember);
  return { local, remote };
};

export const useOpponentCallProfile = ({
  matrixRoom,
  destUserInfo,
  activeCall,
  client,
}) =>
  useMemo(() => {
    let roomOpponent = null;
    try {
      if (matrixRoom) {
        const myUserId = client?.getUserId?.();
        const members = [
          ...(matrixRoom.getJoinedMembers?.() || []),
          ...(matrixRoom.getMembers?.() || []),
          ...(matrixRoom.currentState?.getMembers?.() || []),
        ]
          .filter((member) => member?.userId)
          .filter(
            (member, index, arr) =>
              arr.findIndex((candidate) => candidate?.userId === member?.userId) === index
          );
        if (members?.length) {
          roomOpponent = members.find((m) => m?.userId && m.userId !== myUserId) || null;
        }
      }
    } catch {
      roomOpponent = null;
    }

    const { local: localParticipantName, remote: remoteParticipantFromRoom } =
      getCallParticipantDisplayNames(matrixRoom, client, roomOpponent?.userId);

    const isPlaceholderName = (v) => {
      const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
      return s === 'unknown' || s === '@unknown';
    };

    const infoName = typeof destUserInfo?.username === 'string' ? destUserInfo.username : null;
    const safeInfoName =
      infoName && !isPlaceholderName(infoName) && !looksLikeOpaqueTechnicalId(infoName)
        ? infoName
        : null;

    let opponentDisplayName = null;
    if (remoteParticipantFromRoom) {
      opponentDisplayName = remoteParticipantFromRoom;
    } else if (safeInfoName) {
      opponentDisplayName = safeInfoName;
    } else {
      try {
        const m = activeCall?.getOpponentMember?.() || roomOpponent;
        const fromMember = memberDisplayNameForUi(m);
        if (fromMember) {
          opponentDisplayName = fromMember;
        } else {
          const raw = m?.rawDisplayName || m?.name;
          if (raw && !looksLikeOpaqueTechnicalId(String(raw))) {
            opponentDisplayName = raw;
          } else {
            const id = m?.userId;
            opponentDisplayName = id ? String(id).split(':')[0]?.replace(/^@/, '') : null;
          }
        }
      } catch {
        opponentDisplayName = null;
      }
    }

    let opponentAvatar = DEFAULT_AVATAR;
    if (destUserInfo?.profile_picture && destUserInfo.profile_picture !== DEFAULT_AVATAR) {
      opponentAvatar = destUserInfo.profile_picture;
    } else {
      try {
        const m = activeCall?.getOpponentMember?.() || roomOpponent;
        const avatar = m?.getAvatarUrl?.(client?.baseUrl, 160, 160, 'scale', true);
        opponentAvatar = avatar || DEFAULT_AVATAR;
      } catch {
        opponentAvatar = DEFAULT_AVATAR;
      }
    }

    return { localParticipantName, opponentDisplayName, opponentAvatar };
  }, [matrixRoom, destUserInfo, activeCall, client]);
